/**
 * LIBRARY mode (right column): what you can DO with the clips ticked in the list, and the two file
 * doors into the library.
 *
 * Three sections, in the order a user meets them:
 *  1. **Selection** — every action that takes a set of clips: download them as runtime `.json`
 *     files or as one set, file them, duplicate them, delete them. There is no clip list here any
 *     more; the ticks come from the list in the left column (`librarySelection`), so the library
 *     is drawn once instead of three times (audit B22).
 *  2. **Import animation file** — Mixamo / Meshy / Blender animations into the library.
 *  3. **Backup** — the whole library, folders included. Deliberately last and deliberately named
 *     "backup": it is not what a game loads, and the two used to be confused for each other.
 *
 * `librarySelection` is NOT `library.selectedClipIds`. That store field is the EXPORT-mode embed
 * list ("which clips travel inside the character GLB"), a different question with a different
 * answer; writing to it from here would silently rewrite the user's export set every time they
 * downloaded a file.
 */

import type { LibraryImportMode, LibraryImportPreview } from '../../persistence';
import { DEFAULT_FOLDER } from '../../shared';
import { DEFAULT_DECIMATION_DEGREES } from '../../rig/keyDecimate';
import type { EditorContext } from '../context';
import type { LibraryClipSummary } from '../types';
import type { AnimationImportPreview, Controllers } from '../controllers/types';
import { dialogs } from '../dialogs';
import { button, el, fileButton, icon, numberInput, select } from './dom';
import { librarySelection } from './librarySelection';
import { createPanel, field, note, runAsync, section, type PanelHandle } from './panel';

const MODES: ReadonlyArray<{ value: LibraryImportMode; label: string }> = [
  { value: 'overwrite', label: 'overwrite existing' },
  { value: 'skip', label: 'skip existing' },
  { value: 'copy', label: 'copy as new' },
];

/** The placeholder option of the folder picker — `select()` drops a value it has no option for. */
const CHOOSE = '';

/** `General` first, then the user's folders, then any folder only a clip knows about. */
function folderOrder(clips: readonly LibraryClipSummary[], folders: readonly string[]): string[] {
  const order = [DEFAULT_FOLDER, ...folders];
  const seen = new Set(order);
  for (const clip of clips) {
    if (seen.has(clip.folder)) continue;
    seen.add(clip.folder);
    order.push(clip.folder);
  }
  return order;
}

export function mountLibraryFilesPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  let pendingText: string | null = null;
  let preview: LibraryImportPreview | null = null;
  let mode: LibraryImportMode = 'overwrite';
  let importing = false;

  // Ready-made animation import. Panel-local like the JSON preview above: the parsed three.js
  // objects stay inside the controller, and none of this belongs in the store.
  let animationPreview: AnimationImportPreview | null = null;
  let animationError: string | null = null;
  let animationSelection = new Set<number>();
  let animationNames = new Map<number, string>();
  let animationTolerance = DEFAULT_DECIMATION_DEGREES;
  let animationImporting = false;

  // --- selection --------------------------------------------------------------------------------

  /** The library in display order, so a batch acts in the order shown on screen. */
  function orderedClips(): LibraryClipSummary[] {
    const state = store.getState();
    const clips = state.library.clips;
    const rows: LibraryClipSummary[] = [];
    for (const folder of folderOrder(clips, state.library.folders)) {
      for (const clip of clips) if (clip.folder === folder) rows.push(clip);
    }
    return rows;
  }

  /** Ticked ids, intersected with what still exists — a deleted clip must not reach a download. */
  function pickedIds(): string[] {
    return orderedClips()
      .filter((clip) => librarySelection.has(clip.id))
      .map((clip) => clip.id);
  }

  async function downloadSet(ids: readonly string[]): Promise<void> {
    const name = await dialogs.text({
      title: 'Download as set',
      label: 'File name',
      value: 'clips-set',
    });
    if (!name) return;
    await controllers.library.downloadClipSet(ids, name);
  }

  /*
   * The batches below run one call at a time on purpose: each controller call ends in a
   * `refresh()`, and a serial chain means one settled library per step instead of N refreshes
   * racing each other into the same render.
   *
   * That serial chain is exactly what made a two-clip move look like a one-clip move (tester T11):
   * between step 1 and step 2 the library is HALF moved and the app has no work in flight, so
   * `whenIdle()` returns and a reader sees a torn library. Nothing was lost — but nothing said
   * "still working" either. `batch()` is that missing statement: while it runs, every action in
   * this block is disabled and a busy line names what is happening, so the in-between state is
   * visible in the DOM instead of being indistinguishable from a failure.
   */
  let batchLabel: string | null = null;

  async function batch(
    label: string,
    ids: readonly string[],
    step: (id: string) => Promise<void>,
  ): Promise<void> {
    if (batchLabel !== null || ids.length === 0) return;
    batchLabel = `${label} ${ids.length} clip${ids.length === 1 ? '' : 's'}…`;
    instance.schedule();
    try {
      for (const id of ids) await step(id);
    } finally {
      batchLabel = null;
      instance.schedule();
    }
  }

  function moveAll(ids: readonly string[], folder: string): Promise<void> {
    return batch('Moving', ids, (id) => controllers.library.moveClipToFolder(id, folder));
  }

  function duplicateAll(ids: readonly string[]): Promise<void> {
    return batch('Duplicating', ids, (id) => controllers.library.duplicateClip(id));
  }

  async function deleteAll(ids: readonly string[]): Promise<void> {
    // One question for the batch: three clips must not mean three modal dialogs.
    const ok = await dialogs.confirm({
      title: 'Delete clips',
      message: `Delete ${ids.length} clip(s) from the library?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await batch('Deleting', ids, (id) => controllers.library.deleteClip(id));
  }

  function selectionRows(chosen: readonly string[]): Array<HTMLElement | null> {
    const state = store.getState();
    const total = state.library.clips.length;
    // Nothing ticked, or a batch already in flight: the actions are not available either way.
    const empty = chosen.length === 0 || batchLabel !== null;

    const mover = select(
      [
        { value: CHOOSE, label: '— choose —' },
        ...folderOrder(state.library.clips, state.library.folders).map((name) => ({
          value: name,
          label: name,
        })),
      ],
      CHOOSE,
      (value) => {
        if (value === CHOOSE) return;
        // Back to the placeholder: the picker is an action, not a state — and the rebuild that
        // follows the move would otherwise be the only thing resetting it.
        mover.value = CHOOSE;
        runAsync(moveAll(chosen, value));
      },
      {
        'data-action': 'move-clip',
        title: 'Move every selected clip to a folder',
        disabled: empty,
      },
    );

    return [
      note('Clip files for the runtime SDK. Single files and sets share one format.'),
      el('div', { class: 'buttons' }, [
        button(
          [icon('download'), 'Download files'],
          () => runAsync(controllers.library.downloadClipFiles(chosen)),
          { class: 'primary', 'data-action': 'download-files', disabled: empty },
        ),
        button([icon('download'), 'Download as set…'], () => runAsync(downloadSet(chosen)), {
          'data-action': 'download-set',
          disabled: empty,
        }),
      ]),
      field('Move to folder', mover),
      el('div', { class: 'buttons' }, [
        button([icon('copy'), 'Duplicate'], () => runAsync(duplicateAll(chosen)), {
          'data-action': 'duplicate-clips',
          disabled: empty,
        }),
        button([icon('trash'), 'Delete'], () => runAsync(deleteAll(chosen)), {
          'data-action': 'delete-clips',
          disabled: empty,
        }),
      ]),
      note(`${chosen.length} of ${total} clips selected`, 'note status'),
      // Named `[data-role="batch"]` so a headless check can wait for the batch instead of guessing
      // (`whenIdle()` cannot see it — it flushes persistence, and a batch is between two writes).
      batchLabel === null
        ? null
        : el('p', { class: 'note busy', 'data-role': 'batch', text: batchLabel }),
    ];
  }

  // --- library backup / restore ---------------------------------------------------------------

  function readFile(file: File): void {
    file.text().then(
      (text) => {
        pendingText = text;
        controllers.library.previewImport(text).then(
          (result) => {
            preview = result;
            instance.schedule();
          },
          (error: unknown) => {
            preview = { ok: false, errors: [String(error)] };
            instance.schedule();
          },
        );
      },
      (error: unknown) => {
        pendingText = null;
        preview = { ok: false, errors: [`Could not read the file: ${String(error)}`] };
        instance.schedule();
      },
    );
  }

  function commit(): void {
    if (!pendingText || importing) return;
    importing = true;
    instance.schedule();
    const text = pendingText;
    controllers.library.commitImport(text, mode).then(
      (result) => {
        importing = false;
        pendingText = null;
        preview = null;
        store.actions.toast(
          'info',
          `Imported ${result.imported.length}, overwritten ${result.overwritten.length}, skipped ${result.skipped.length}, copied ${result.copied.length}`,
        );
        instance.schedule();
      },
      () => {
        importing = false;
        instance.schedule();
      },
    );
  }

  function importRows(): HTMLElement[] {
    const rows: HTMLElement[] = [
      el('div', { class: 'buttons' }, [
        button(
          [icon('download'), 'Export library JSON'],
          () => runAsync(controllers.library.exportJson()),
          { 'data-action': 'export-library' },
        ),
        fileButton('Restore…', '.json', readFile, { 'data-role': 'library-input' }),
      ]),
    ];

    if (preview && !preview.ok) {
      const list = el('ul', { class: 'errlist', 'data-role': 'import-errors' });
      for (const error of preview.errors) list.appendChild(el('li', { text: error }));
      rows.push(el('p', { class: 'note bad', text: 'File rejected — nothing was written.' }));
      rows.push(list);
    }

    if (preview && preview.ok) {
      const summary = preview.summary;
      rows.push(
        el('p', {
          class: 'note',
          'data-role': 'import-summary',
          text: `${summary.new.length} new · ${summary.existing.length} existing · ${summary.identical.length} identical`,
        }),
      );
      if (preview.warnings.length > 0) {
        const list = el('ul', { class: 'warnlist' });
        for (const warning of preview.warnings) list.appendChild(el('li', { text: warning }));
        rows.push(list);
      }
      const modes = el('div', { class: 'radios' });
      for (const option of MODES) {
        const radio = el('input', { type: 'radio', name: 'import-mode', value: option.value });
        radio.checked = mode === option.value;
        radio.addEventListener('change', () => {
          if (radio.checked) mode = option.value;
        });
        modes.appendChild(el('label', { class: 'check' }, [radio, ' ', option.label]));
      }
      rows.push(modes);
      rows.push(
        el('div', { class: 'buttons' }, [
          button(importing ? [icon('upload'), 'Importing…'] : [icon('upload'), 'Import'], commit, {
            disabled: importing,
            'data-action': 'commit-import',
          }),
          button([icon('reset'), 'Cancel'], () => {
            pendingText = null;
            preview = null;
            instance.schedule();
          }),
        ]),
      );
    }

    return rows;
  }

  // --- ready-made animation import (Mixamo .fbx / Meshy .glb) ---------------------------------

  function readAnimationFile(file: File): void {
    animationPreview = null;
    animationError = null;
    animationSelection = new Set();
    animationNames = new Map();
    instance.schedule();

    controllers.animationImport.preview(file).then(
      (result) => {
        animationPreview = result;
        // Everything in the file is selected by default: a Mixamo download holds exactly one clip
        // and making the user tick it would be pure ceremony.
        animationSelection = new Set(result.clips.map((c) => c.index));
        animationNames = new Map(result.clips.map((c) => [c.index, c.name]));
        instance.schedule();
      },
      (error: unknown) => {
        animationError = error instanceof Error ? error.message : String(error);
        instance.schedule();
      },
    );
  }

  function commitAnimation(): void {
    if (!animationPreview || animationImporting) return;
    animationImporting = true;
    instance.schedule();

    const selection = [...animationSelection]
      .sort((a, b) => a - b)
      .map((index) => ({ index, name: animationNames.get(index) ?? `Clip ${index + 1}` }));

    controllers.animationImport
      .commit(selection, { toleranceDegrees: animationTolerance })
      .then(
        () => {
          animationImporting = false;
          animationPreview = null;
          animationSelection = new Set();
          animationNames = new Map();
          instance.schedule();
        },
        () => {
          animationImporting = false;
          instance.schedule();
        },
      );
  }

  function animationRows(): HTMLElement[] {
    const rows: HTMLElement[] = [];

    // A drop target as well as a button: dropping a .glb here must import an ANIMATION, which is
    // why the window-level handler in `app.ts` skips anything dropped inside a `.dropzone`.
    const drop = el('div', { class: 'dropzone' }, [
      el('div', { text: 'Mixamo .fbx · Meshy / Blender .glb' }),
      fileButton('Choose file…', '.fbx,.glb,.gltf', readAnimationFile, {
        'data-role': 'animation-input',
      }),
      el('div', { class: 'dim', text: 'or drop it here' }),
    ]);
    drop.addEventListener('dragover', (event) => {
      event.preventDefault();
      drop.classList.add('over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('over'));
    drop.addEventListener('drop', (event) => {
      event.preventDefault();
      drop.classList.remove('over');
      const file = event.dataTransfer?.files?.[0];
      if (file) readAnimationFile(file);
    });
    rows.push(drop);
    rows.push(note('The animation is stored for every character, not just this one.'));

    if (animationError) {
      rows.push(el('p', { class: 'note bad', text: 'File rejected — nothing was written.' }));
      rows.push(
        el('ul', { class: 'errlist', 'data-role': 'animation-errors' }, [
          el('li', { text: animationError }),
        ]),
      );
    }

    if (!animationPreview) return rows;

    rows.push(
      el('p', {
        class: 'note',
        'data-role': 'animation-summary',
        text:
          `${animationPreview.format.toUpperCase()} · ${animationPreview.clips.length} animation(s) · ` +
          `${animationPreview.mappedBoneCount} bones matched`,
      }),
    );

    if (animationPreview.warnings.length > 0) {
      const list = el('ul', { class: 'warnlist' });
      for (const warning of animationPreview.warnings) list.appendChild(el('li', { text: warning }));
      rows.push(list);
    }

    for (const clip of animationPreview.clips) {
      const box = el('input', { type: 'checkbox', title: 'Import this animation' });
      box.checked = animationSelection.has(clip.index);
      box.addEventListener('change', () => {
        if (box.checked) animationSelection.add(clip.index);
        else animationSelection.delete(clip.index);
        instance.schedule();
      });

      // `input`, not `change`: the name has to be current when the Import button is pressed, and
      // pressing it is exactly the blur that a `change` listener would be waiting for.
      const nameInput = el('input', {
        type: 'text',
        value: animationNames.get(clip.index) ?? clip.name,
        title: 'Name in the library',
      });
      nameInput.addEventListener('input', () => {
        animationNames.set(clip.index, nameInput.value);
      });

      rows.push(
        el('div', { class: 'row', 'data-role': 'animation-clip' }, [
          box,
          nameInput,
          el('span', {
            class: 'dim',
            text:
              `${clip.durationSeconds.toFixed(2)}s @ ${clip.fps ?? '?'} fps` +
              (clip.hasRootMotion ? ' · moves' : ''),
          }),
        ]),
      );
    }

    rows.push(
      field(
        'Simplify (°)',
        numberInput(
          animationTolerance,
          (value) => {
            const clamped = Math.min(10, Math.max(0, value));
            animationTolerance = clamped;
            // Only when the typed number was out of range: otherwise the rebuild is pure churn.
            if (clamped !== value) instance.schedule();
          },
          { min: '0', max: '10', step: '0.1', title: '0 keeps every frame' },
        ),
      ),
    );

    rows.push(
      el('div', { class: 'buttons' }, [
        button(
          animationImporting ? [icon('upload'), 'Importing…'] : [icon('upload'), 'Import animation'],
          commitAnimation,
          {
            disabled: animationImporting || animationSelection.size === 0,
            'data-action': 'commit-animation',
          },
        ),
        button([icon('reset'), 'Cancel'], () => {
          controllers.animationImport.cancel();
          animationPreview = null;
          animationError = null;
          instance.schedule();
        }),
      ]),
    );

    return rows;
  }

  function render(root: HTMLElement): void {
    const chosen = pickedIds();

    root.appendChild(
      section('Selection', selectionRows(chosen), {
        icon: 'check',
        key: 'files-selection',
        meta: `${chosen.length} clips`,
      }),
    );
    root.appendChild(
      section('Import animation file', animationRows(), {
        icon: 'upload',
        key: 'files-import-anim',
      }),
    );
    root.appendChild(
      section(
        'Backup',
        [
          note('The whole library, folders included. For safekeeping — not what a game loads.'),
          ...importRows(),
        ],
        { icon: 'save', key: 'files-backup' },
      ),
    );
  }

  const instance = createPanel(container, ctx, render, {
    className: 'library-files',
    slices: [(s) => s.library.clips, (s) => s.library.folders],
  });

  // The ticks live in the left column's list; this panel is the actions on them.
  instance.own(librarySelection.subscribe(() => instance.schedule()));

  return instance;
}
